/**
 * PaperMetricsDashboard — performance metrics display and event log for a paper session.
 */

import { useState, useMemo } from 'react';
import { Dashboard } from '../Dashboard';
import type { PerformanceMetrics } from '../../types';

// ============================================================================
// EventTypeBadge — color-coded pill for event type
// ============================================================================

function EventTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    trade_opened: 'bg-green-900/50 text-green-400',
    trade_closed: 'bg-blue-900/50 text-blue-400',
    trade_rejected: 'bg-orange-900/50 text-orange-400',
    funding_payment: 'bg-purple-900/50 text-purple-400',
    error: 'bg-red-900/50 text-red-400',
    retry: 'bg-yellow-900/50 text-yellow-400',
    status_change: 'bg-indigo-900/50 text-indigo-400',
    kill_switch_triggered: 'bg-red-900/70 text-red-300',
  };
  const labels: Record<string, string> = {
    trade_opened: 'Open',
    trade_closed: 'Close',
    trade_rejected: 'Rejected',
    funding_payment: 'Funding',
    error: 'Error',
    retry: 'Retry',
    status_change: 'Status',
    kill_switch_triggered: 'Kill Switch',
  };

  const style = styles[type] ?? 'bg-gray-700 text-gray-400';
  const label = labels[type] ?? type;

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${style}`}>
      {label}
    </span>
  );
}

// ============================================================================
// SessionEvent type (local, matching the API shape)
// ============================================================================

interface SessionEventRecord {
  id: number;
  createdAt: number;
  type: string;
  message: string;
}

interface EventsData {
  events: SessionEventRecord[];
  total: number;
}

// ============================================================================
// PaperMetricsDashboard
// ============================================================================

interface PaperMetricsDashboardProps {
  metrics: PerformanceMetrics | null;
  eventsData: EventsData | undefined;
  /**
   * SHA-72: true for a LIVE (real-money) session, e.g. `connectorType ===
   * 'bybit'`. Threaded through to <Dashboard> to suppress the "hypothetical
   * results" disclaimer for live sessions — those results are real, not
   * simulated. Defaults to false (disclaimer shown), correct for paper
   * sessions.
   */
  isLive?: boolean;
}

const ALL_EVENT_TYPES = ['trade_opened', 'trade_closed', 'trade_rejected', 'funding_payment', 'error', 'retry', 'status_change', 'kill_switch_triggered'] as const;

export function PaperMetricsDashboard({ metrics, eventsData, isLive = false }: PaperMetricsDashboardProps) {
  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(new Set(ALL_EVENT_TYPES));

  const filteredEvents = useMemo(() => {
    if (!eventsData) return [];
    return eventsData.events.filter((evt) => enabledTypes.has(evt.type));
  }, [eventsData, enabledTypes]);

  const toggleType = (type: string) => {
    setEnabledTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Collect types actually present in events
  const presentTypes = useMemo(() => {
    if (!eventsData) return [] as string[];
    const seen = new Set<string>();
    for (const evt of eventsData.events) seen.add(evt.type);
    return ALL_EVENT_TYPES.filter((t) => seen.has(t));
  }, [eventsData]);

  return (
    <>
      {/* Dashboard metrics */}
      <section>
        <Dashboard metrics={metrics} isLive={isLive} />
      </section>

      {/* Event Log */}
      {eventsData && eventsData.events.length > 0 && (
        <section className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">
              Event Log ({filteredEvents.length}/{eventsData.total})
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {presentTypes.map((type) => {
                const active = enabledTypes.has(type);
                return (
                  <label
                    key={type}
                    className={`flex items-center gap-1 cursor-pointer select-none text-[10px] font-medium px-1.5 py-0.5 rounded transition-opacity ${
                      active ? 'opacity-100' : 'opacity-40'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleType(type)}
                      className="w-3 h-3 rounded accent-primary-500"
                    />
                    <EventTypeBadge type={type} />
                  </label>
                );
              })}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filteredEvents.map((evt) => (
              <div
                key={evt.id}
                className="flex items-start gap-2 text-xs py-1 border-b border-gray-700/30 last:border-0"
              >
                <span className="text-gray-500 shrink-0 w-[140px]">
                  {new Date(evt.createdAt).toLocaleString()}
                </span>
                <EventTypeBadge type={evt.type} />
                <span className="text-gray-300">{evt.message}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
